import { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'
import DocumentPreview from '../components/DocumentPreview'

interface Document {
  PK?: string
  doc_id?: string
  title?: string
  type?: string
  owner?: string
  status?: string
  quality?: number
  risk?: string
  created_at?: string
  updated_at?: string
  review_comment?: string
  filename?: string
  mime_type?: string
  doc_type?: string
  confidence?: string | number
  assessment?: string | { summary?: string; quality?: string; detected_fields?: Record<string, string> }
  drive_url?: string
  drive_folder_url?: string
  drive_folder_path?: string
}

interface DocsResponse {
  documents: Document[]
  count: number
}

interface DriveFolder { id: string; name: string; url: string; children?: DriveFolder[] }
interface DriveTree { root: DriveFolder; folders: DriveFolder[]; truncated?: boolean }

const STATUS_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  UPLOADED:   { bg: 'var(--c-surface-faint)',  color: 'var(--c-ink-muted)', label: 'Subido' },
  PROCESSING: { bg: 'var(--c-info-light)',   color: 'var(--c-info-ink)',   label: 'Procesando' },
  READY:      { bg: 'var(--c-warn-light)',    color: 'var(--c-warn)',   label: 'Listo' },
  PUBLISHED:  { bg: 'var(--c-brand-light)',   color: 'var(--c-brand-hover)',   label: 'Publicado' },
  REJECTED:   { bg: 'var(--c-danger-light)',    color: 'var(--c-danger)',    label: 'Rechazado' },
  RETIRED:    { bg: 'var(--c-surface-faint)',  color: 'var(--c-ink-muted)', label: 'Retirado' },
  // Faltaban los cuatro estados que de verdad recorre un documento del
  // TeleAgent. Sin ellos caían al fallback `UPLOADED` y la pantalla decía
  // «Subido» para todo: el asesor aprobaba y el chip no cambiaba, así que
  // parecía que el botón no hacía nada.
  NEEDS_REVIEW:      { bg: 'var(--c-warn-light)',   color: 'var(--c-warn)',   label: 'Por revisar' },
  AWAITING_REVIEW:   { bg: 'var(--c-warn-light)',   color: 'var(--c-warn)',   label: 'Por revisar' },
  APPROVED:          { bg: 'var(--c-brand-light)',  color: 'var(--c-brand-hover)',   label: 'Aprobado' },
  UPLOADED_TO_DRIVE: { bg: 'var(--c-brand-light)',  color: 'var(--c-brand-hover)',   label: 'En Drive' },
  PROCESSING_FAILED: { bg: 'var(--c-danger-light)', color: 'var(--c-danger)',        label: 'Falló el proceso' },
  SEPARADO:          { bg: 'var(--c-surface-faint)', color: 'var(--c-ink-muted)', label: 'Separado en partes' },
}

const RISK_COLOR: Record<string, string> = {
  ALTO: 'var(--c-danger)', MEDIO: 'var(--c-warn-dot)', BAJO: 'var(--c-brand)'
}

function fmtDate(ts?: string) {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' })
}

function assessmentOf(doc: Document) {
  if (!doc.assessment) return {}
  if (typeof doc.assessment !== 'string') return doc.assessment
  try { return JSON.parse(doc.assessment) as { summary?: string; quality?: string; detected_fields?: Record<string, string> } }
  catch { return {} }
}

export default function Documents() {
  const { canSupervise, isAdmin } = useAuth()
  const canReview = canSupervise
  const [docs, setDocs] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Document | null>(null)
  const [comment, setComment] = useState('')
  const [actionLoading, setActionLoading] = useState('')
  const [actionError, setActionError] = useState<string | null>(null)
  const [driveTree, setDriveTree] = useState<DriveTree | null>(null)

  const fetchDocs = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await apiClient.get<DocsResponse>('/documents?limit=50')
      setDocs(r.data.documents ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar documentos')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { void fetchDocs() }, [fetchDocs])
  useEffect(() => {
    void apiClient.get<DriveTree>('/integrations/drive/tree').then(r => setDriveTree(r.data)).catch(() => setDriveTree(null))
  }, [])

  const filtered = useMemo(() => {
    if (!search.trim()) return docs
    const q = search.toLowerCase()
    return docs.filter((d) => [d.title, d.type, d.owner].some((v) => v?.toLowerCase().includes(q)))
  }, [docs, search])

  const doAction = async (action: string) => {
    const id = selected?.doc_id ?? selected?.PK?.replace('DOC#', '') ?? ''
    if (!id) return
    setActionLoading(action); setActionError(null)
    try {
      const decision = action === 'approve' || action === 'publish' ? 'APPROVED' : 'REJECTED'
      await apiClient.post(`/documents/${id}/review`, { decision, notes: comment, reviewer_id: 'portal' })
      const response = await apiClient.get<DocsResponse>('/documents?limit=50')
      const updatedDocs = response.data.documents ?? []
      setDocs(updatedDocs)
      setSelected(updatedDocs.find((d) => (d.doc_id ?? d.PK?.replace('DOC#', '')) === id) ?? null)
      setComment('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al procesar')
    } finally { setActionLoading('') }
  }

  if (loading) return <LoadingSpinner label="Cargando documentos..." />
  if (error)   return <ErrorAlert message={error} onRetry={fetchDocs} />

  const selSt = selected?.status ? (STATUS_STYLE[selected.status] ?? STATUS_STYLE['UPLOADED']) : null
  const assessment = selected ? assessmentOf(selected) : {}
  const selectedId = selected?.doc_id ?? selected?.PK?.replace('DOC#', '') ?? ''

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
        <input type="search" placeholder="Buscar documentos..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          className="input" style={{ maxWidth: '360px' }} />
        {isAdmin && (
          <button onClick={() => alert('Carga de documentos próximamente — S3 presigned URLs.')}
            style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-brand)',
                     color: 'white', fontWeight: 700, fontSize: '13px',
                     padding: '9px 18px', borderRadius: '10px' }}>
            + Cargar documento
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="📄" title={search ? 'Sin resultados' : 'Sin documentos'}
          description="Los documentos aprobados son usados por el agente para responder consultas." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px',
                      height: '72vh', minHeight: '540px' }}>

          {/* Lista lateral */}
          <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                        borderRadius: '16px', overflowY: 'auto' }}>
            {filtered.map((doc, i) => {
              const id = doc.doc_id ?? doc.PK ?? String(i)
              const st = STATUS_STYLE[doc.status ?? ''] ?? STATUS_STYLE['UPLOADED']
              const isSelected = (selected?.doc_id ?? selected?.PK) === id
              return (
                <div key={id} onClick={() => { setSelected(doc); setComment('') }}
                  style={{ padding: '14px 16px', borderBottom: '1px solid var(--c-surface-faint)',
                           cursor: 'pointer', background: isSelected ? 'var(--c-surface-subtle)' : 'white' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {doc.title ?? '—'}
                  </div>
                  <div style={{ fontSize: '11.5px', color: 'var(--c-ink-muted)', margin: '3px 0 7px' }}>
                    {doc.owner ?? '—'} · {fmtDate(doc.created_at)}
                  </div>
                  <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                                 background: st.bg, color: st.color }}>
                    {st.label}
                  </span>
                </div>
              )
            })}
          </div>

          {/* Panel derecho */}
          {!selected ? (
            <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                          borderRadius: '16px', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', color: 'var(--c-ink-muted)', fontSize: '13px' }}>
              Selecciona un documento para revisar
            </div>
          ) : (
            <div style={{ background: 'white', border: '1px solid var(--c-surface-border)',
                          borderRadius: '16px', padding: '22px', display: 'flex',
                          flexDirection: 'column', gap: '18px', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 800 }}>{selected.title ?? 'Documento'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--c-ink-muted)' }}>
                    Propietario: {selected.owner ?? '—'}
                  </div>
                </div>
                {selSt && (
                  <span style={{ fontSize: '10.5px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                                 background: selSt.bg, color: selSt.color }}>
                    {selSt.label}
                  </span>
                )}
              </div>

              {/* Preview + fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '18px' }}>
                <DocumentPreview documentId={selectedId} filename={selected.filename ?? selected.title} />
                <div style={{ background: 'repeating-linear-gradient(135deg, var(--c-surface-subtle), var(--c-surface-subtle) 10px, var(--c-surface-border) 10px, var(--c-surface-border) 20px)',
                              borderRadius: '12px', height: '230px', display: 'none',
                              alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '11.5px',
                                 color: 'var(--c-ink-muted)', background: 'white',
                                 padding: '6px 12px', borderRadius: '8px' }}>
                    vista previa · {selected.title}
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em',
                                  color: 'var(--c-ink-muted)', marginBottom: '8px' }}>CAMPOS</div>
                    {[
                      { k: 'Tipo detectado', v: selected.type },
                      { k: 'Confianza', v: selected.confidence != null ? `${selected.confidence}%` : undefined },
                      { k: 'Calidad de lectura', v: assessment.quality },
                      { k: 'Fecha',   v: fmtDate(selected.created_at) },
                      { k: 'Comentario', v: selected.review_comment },
                      { k: 'Resultado', v: assessment.summary },
                    ].filter((f) => f.v).map((f) => (
                      <div key={f.k} style={{ display: 'flex', justifyContent: 'space-between',
                                              fontSize: '12.5px', padding: '6px 0',
                                              borderBottom: '1px solid var(--c-surface-subtle)' }}>
                        <span style={{ color: 'var(--c-ink-muted)' }}>{f.k}</span>
                        <span style={{ fontWeight: 600 }}>{f.v}</span>
                      </div>
                    ))}
                  </div>
                  {Object.keys(assessment.detected_fields ?? {}).length > 0 && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--c-ink-muted)', marginBottom: '6px' }}>DATOS EXTRAIDOS</div>
                      {Object.entries(assessment.detected_fields ?? {}).slice(0, 6).map(([key, value]) => (
                        <div key={key} style={{ display: 'flex', gap: '8px', fontSize: '11.5px', padding: '4px 0', borderBottom: '1px solid var(--c-surface-subtle)' }}>
                          <span style={{ color: 'var(--c-ink-muted)' }}>{key}</span><span style={{ fontWeight: 600 }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {(selected.drive_folder_path || selected.drive_folder_url) && (
                    <a href={selected.drive_folder_url || selected.drive_url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', color: 'var(--c-info-ink)', fontWeight: 700 }}>
                      Abrir carpeta en Drive {selected.drive_folder_path ? `- ${selected.drive_folder_path}` : ''}
                    </a>
                  )}
                  <div style={{ display: 'flex', gap: '20px' }}>
                    {selected.quality != null && (
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--c-ink-muted)' }}>Calidad</div>
                        <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: '16px' }}>
                          {selected.quality}%
                        </div>
                      </div>
                    )}
                    {selected.risk && (
                      <div>
                        <div style={{ fontSize: '11px', color: 'var(--c-ink-muted)' }}>Riesgo</div>
                        <div style={{ fontWeight: 700, fontSize: '13px', color: RISK_COLOR[selected.risk] ?? 'inherit' }}>
                          {selected.risk}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Actions */}
              {canReview && (
                <>
                  {actionError && (
                    <div style={{ fontSize: '12px', color: 'var(--c-danger)', padding: '8px 12px',
                                  background: 'var(--c-danger-light)', borderRadius: '8px' }}>
                      {actionError}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder="Observaciones de revisión…"
                      style={{ flex: 1, border: '1px solid var(--c-surface-strong)', borderRadius: '10px',
                               padding: '10px 14px', fontSize: '13px', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                    <button disabled={!!actionLoading} onClick={() => void doAction('reject')}
                      style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-danger)', color: 'white', fontWeight: 700, fontSize: '13px', padding: '10px 18px', borderRadius: '10px', opacity: actionLoading ? 0.6 : 1 }}>
                      {actionLoading === 'reject' ? '...' : 'Desaprobar'}
                    </button>
                    <button disabled={!!actionLoading} onClick={() => void doAction('approve')}
                      style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-brand)', color: 'white', fontWeight: 700, fontSize: '13px', padding: '10px 18px', borderRadius: '10px', opacity: actionLoading ? 0.6 : 1 }}>
                      {actionLoading === 'approve' ? '...' : 'Aprobar'}
                    </button>
                    {(selected.status === 'READY' || selected.status === 'UPLOADED') && (
                      <button disabled={!!actionLoading} onClick={() => void doAction('reject')}
                        style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-danger)',
                                 color: 'white', fontWeight: 700, fontSize: '13px',
                                 padding: '10px 18px', borderRadius: '10px', opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading === 'reject' ? '…' : 'Rechazar'}
                      </button>
                    )}
                    {selected.status === 'READY' && (
                      <button disabled={!!actionLoading} onClick={() => void doAction('approve')}
                        style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-brand)',
                                 color: 'white', fontWeight: 700, fontSize: '13px',
                                 padding: '10px 18px', borderRadius: '10px', opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading === 'approve' ? '…' : 'Aprobar'}
                      </button>
                    )}
                    {selected.status === 'UPLOADED' && (
                      <button disabled={!!actionLoading} onClick={() => void doAction('publish')}
                        style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-info)',
                                 color: 'white', fontWeight: 700, fontSize: '13px',
                                 padding: '10px 18px', borderRadius: '10px', opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading === 'publish' ? '…' : 'Publicar'}
                      </button>
                    )}
                    {selected.status === 'PUBLISHED' && (
                      <button disabled={!!actionLoading} onClick={() => void doAction('retire')}
                        style={{ all: 'unset', cursor: 'pointer', background: 'var(--c-surface-faint)',
                                 color: 'var(--c-ink-muted)', fontWeight: 700, fontSize: '13px',
                                 padding: '10px 18px', borderRadius: '10px',
                                 border: '1px solid var(--c-surface-strong)', opacity: actionLoading ? 0.6 : 1 }}>
                        {actionLoading === 'retire' ? '…' : 'Retirar'}
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
      {driveTree && (
        <div style={{ background: 'white', border: '1px solid var(--c-surface-border)', borderRadius: '14px', padding: '14px 18px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.06em', color: 'var(--c-ink-muted)', marginBottom: '8px' }}>ESTRUCTURA ACTUAL EN DRIVE</div>
          <a href={driveTree.root.url} target="_blank" rel="noreferrer" style={{ color: 'var(--c-info-ink)', fontWeight: 700, fontSize: '13px' }}>{driveTree.root.name}</a>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '9px' }}>
            {driveTree.folders.slice(0, 12).map(folder => (
              <a key={folder.id} href={folder.url} target="_blank" rel="noreferrer" style={{ fontSize: '12px', padding: '5px 9px', borderRadius: '8px', background: 'var(--c-surface-subtle)', color: 'var(--c-ink-soft)' }}>
                {folder.name}{folder.children?.length ? ` / ${folder.children.length}` : ''}
              </a>
            ))}
          </div>
          {driveTree.truncated && <div style={{ marginTop: '8px', fontSize: '11px', color: 'var(--c-ink-muted)' }}>Se muestran las primeras 200 carpetas.</div>}
        </div>
      )}
    </div>
  )
}
